import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import { useState } from 'react';
import querystring from 'querystring';
import { Auth, recordListActions } from '../../config/configuration/react';
import { t, translateDate } from '../../components/common/Translate';
import {
  RECORD_STATUS as statusListConfig,
  datesFormat,
  REACT_APP_API,
  NRM_SUB_STATUS as nrmStatusListConfig,
  decodeVinCountry,
  cancleCases as cancleCasesConfig,
} from '../../config';
import { calendarLang } from '../../language-context';
import { useConcessionsBuyersFilters } from './useConcessionsBuyersFilters';
import { useMakeModelFilters } from './useMakeModelFilters';

export const useFilters = () => {
  const dispatch = useDispatch();
  const appLang = Auth.preferredLanguage();
  const filtersState = useSelector((state) => state.recordList.filters);
  const sortState = useSelector((state) => state.recordList.sort);
  const dateFormat = appLang === 'DE' ? datesFormat[appLang] : datesFormat.default;
  const locale = calendarLang[appLang];
  const mode = decodeVinCountry.includes(Auth.country()) ? 'vin' : 'registration';
  const localStorageFilters = useConcessionsBuyersFilters();
  const concessionsLocalS = localStorageFilters?.concessionsLSearch || [];
  const buyersLocalS = localStorageFilters?.buyersLS || [];
  const sourcesLocalS = localStorageFilters?.sourcesLS || [];
  const userCountry = Auth.country();
  const countriesFilterStatus = ['ES', 'IT', 'PT', 'DE', 'EN', 'NL'];
  const idsFilterStatus = [5, 6, 7, 9, 10, 11, 13, 14, 15];
  const { makeLists, modelLists } = useMakeModelFilters(filtersState);
  const [isOpen, setIsOpen] = useState(false);
  let paramSource = '';
  if (Auth.role() === 'manager') {
    paramSource = `&sourceIds=${Auth.sourceIds().join(',')}`;
  }
  const params = {};
  Object.entries(filtersState).forEach(([key, value]) => {
    if (value.value && value.value !== '' && !Number.isNaN(value.value) && value.value?.[0] !== '') {
      if (key === 'userId') params.buyerId = value.value;
      else params[key] = value.value;
    }
  });
  if (sortState?.length > 0) {
    params.sort = JSON.stringify(sortState);
  }
  const downloadExcel = `${REACT_APP_API}/buyer/public/buyer-inspection-export?${querystring.stringify(
    params,
  )}&userCountry=${userCountry}${paramSource}`;

  const searchWithFilters = async () => {
    await dispatch(recordListActions.resetCountCurrentPage());
    await dispatch(recordListActions.searchWithFilters());
  };

  const resetFilters = async () => {
    await dispatch(recordListActions.resetFilters());
  };

  const setFilter = async (name, value) => {
    await dispatch(recordListActions.setFilter(name, value));
  };

  const setFilterDate = async (name, value) => {
    await dispatch(recordListActions.setFilterDate(name, translateDate(value, t('YYYY-MM-DD'))));
  };

  const concessionsOptions = concessionsLocalS.map((c) => ({
    id: c.id,
    name: c.name,
  }));

  const buyersOptions = buyersLocalS.map((b) => ({
    id: b.id,
    name: b.fullName,
  }));

  const sourcesOptions = sourcesLocalS.map((s) => ({
    id: s.id,
    name: s.source,
  }));

  const statusOptions = [];
  Object.entries(statusListConfig).forEach(([name, id]) => {
    if (
      id > 0 &&
      (userCountry === 'FR' || (countriesFilterStatus.includes(userCountry) && !idsFilterStatus.includes(id)))
    ) {
      statusOptions.push({
        id,
        name,
      });
    }
  });
  const statusOptionsSorted = _.sortBy(statusOptions, 'id');

  const nrmStatusOptions = [];
  Object.entries(nrmStatusListConfig).forEach(([name, id]) => {
    if (id >= 0) {
      nrmStatusOptions.push({ id, name });
    }
  });
  const nrmStatusOptionsSorted = _.sortBy(nrmStatusOptions, 'id');

  const cancelCaseOptions = [];
  Object.entries(cancleCasesConfig).forEach(([name, id]) => {
    cancelCaseOptions.push({ id, name });
  });

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const handleValidate = async () => {
    const a = document.createElement('a');
    a.href = downloadExcel;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toggle();
  };

  const filters = {
    creationDateMin: {
      value: filtersState.creationDateMin.value ? moment(filtersState.creationDateMin.value).toDate() : null,
      error: t(filtersState.creationDateMin.error),
      label: t('creation_date'),
      setValue: (value) => setFilterDate('creationDateMin', value),
      placeholder: t('long_date_placeholder'),
      input: {
        name: 'creationDateMin',
        id: 'creationDateMin',
      },
      locale,
      dateFormat,
    },

    creationDateMax: {
      value: filtersState.creationDateMax.value ? moment(filtersState.creationDateMax.value).toDate() : null,
      error: t(filtersState?.creationDateMax.error),
      label: t('to'),
      setValue: (value) => setFilterDate('creationDateMax', value),
      placeholder: t('long_date_placeholder'),
      input: {
        name: 'creationDateMax',
        id: 'creationDateMax',
      },
      locale,
      dateFormat,
    },

    entryStockDateMin: {
      value: filtersState?.entryStockDateMin?.value ? moment(filtersState?.entryStockDateMin?.value).toDate() : null,
      error: t(filtersState?.entryStockDateMin?.error),
      label: t('entryStockDate'),
      setValue: (value) => setFilterDate('entryStockDateMin', value),
      placeholder: t('long_date_placeholder'),
      input: {
        name: 'entryStockDateMin',
        id: 'entryStockDateMin',
      },
      locale,
      dateFormat,
    },

    entryStockDateMax: {
      value: filtersState?.entryStockDateMax?.value ? moment(filtersState?.entryStockDateMax?.value).toDate() : null,
      error: t(filtersState?.entryStockDateMax?.error),
      label: t('to'),
      setValue: (value) => setFilterDate('entryStockDateMax', value),
      placeholder: t('long_date_placeholder'),
      input: {
        name: 'entryStockDateMax',
        id: 'entryStockDateMax',
      },
      locale,
      dateFormat,
    },

    refHexaId: {
      value: filtersState.refHexaId.value ? filtersState.refHexaId.value : '',
      error: filtersState.refHexaId.error,
      label: t('file_number'),
      setValue: (value) => setFilter('refHexaId', value.replace(/\s+/g, '')),
      placeholder: t('refhexId_placeholder'),
      input: {
        name: 'refHexaId',
        id: 'refHexaId',
      },
    },
    registration: {
      value: filtersState.registration.value ? filtersState.registration.value : '',
      error: filtersState.registration.error,
      label: t('registration'),
      setValue: (value) => setFilter('registration', value),
      placeholder: t('registration_plate_placeholder'),
      input: {
        name: 'registration',
        id: 'registration',
      },
    },
    vin: {
      value: filtersState.vin.value ? filtersState.vin.value : '',
      error: filtersState.vin.error,
      label: t('vin'),
      setValue: (value) => setFilter('vin', value),
      placeholder: 'VF12REL1E5064106',
      input: {
        name: 'vin',
        id: 'vin',
      },
    },
    make: {
      value: filtersState?.brandName?.value ? filtersState.brandName.value : '',
      error: filtersState?.brandName?.error,
      label: t('make'),
      setValue: (value) => {
        if (!value || filtersState?.brandName?.value !== value) setFilter('modelName', null);
        return setFilter('brandName', value);
      },
      placeholder: t('make'),
      options: makeLists,
      input: {
        name: 'make',
        id: 'make',
        label: t('select'),
      },
    },
    model: {
      value: filtersState?.modelName?.value ? filtersState?.modelName?.value : '',
      error: filtersState?.modelName?.error,
      label: t('model'),
      setValue: (value) => setFilter('modelName', value),
      placeholder: t('model'),
      options: modelLists,
      input: {
        name: 'model',
        id: 'model',
        label: t('select'),
      },
    },
    registrationDate: {
      value: filtersState.registrationDate.value ? filtersState.registrationDate.value : '',
      error: filtersState.registrationDate.error,
      label: t('registrationDate'),
      setValue: (value) => setFilter('registrationDate', value),
      placeholder: t('year_placeholder'),
      input: {
        name: 'registrationDate',
        id: 'registrationDate',
      },
    },
    concessionId: {
      value: filtersState.concessionId.value ? filtersState.concessionId.value : '',
      error: filtersState.concessionId.error,
      label: t('concession'),
      setValue: (value) => setFilter('concessionId', parseInt(value, 10)),
      placeholder: t('concessionId'),
      options: concessionsOptions,
      input: {
        name: 'concessionId',
        id: 'concessionId',
        label: t('select_concessions'),
      },
    },
    userId: {
      value: filtersState.userId.value ? filtersState.userId.value : '',
      error: filtersState.userId.error,
      label: t('buyerId'),
      setValue: (value) => setFilter('userId', parseInt(value, 10)),
      placeholder: t('userId'),
      options: buyersOptions,
      input: {
        name: 'userId',
        id: 'userId',
        label: t('select_buyer'),
      },
    },
    status: {
      value: filtersState.status.value ? filtersState.status.value.join(',') : '',
      error: filtersState.status.error,
      label: t('status_form_label'),
      setValue: (value) => setFilter('status', value.split(',')),
      placeholder: t('status'),
      options: statusOptionsSorted,
      input: {
        name: 'status',
        id: 'status',
        label: t('select'),
      },
    },
    clic2sell: {
      value: filtersState?.clic2sell?.value ? filtersState.clic2sell.value : false,
      error: filtersState?.clic2sell?.error,
      label: t('hbs_label'),
      setValue: (value) => setFilter('clic2sell', value),
      input: {
        name: 'clic2sell',
        id: 'clic2sell',
      },
    },
    clic2buy: {
      value: filtersState?.clic2buy?.value ? filtersState.clic2buy.value : false,
      error: filtersState?.clic2buy?.error,
      label: t('clic2Buy_Label'),
      setValue: (value) => setFilter('clic2buy', value),
      input: {
        name: 'clic2buy',
        id: 'clic2buy',
      },
    },
    recordTypeId: {
      value: filtersState?.recordTypeId?.value ? filtersState.recordTypeId.value : false,
      error: filtersState?.recordTypeId?.error,
      label: t('counterAppraisalLabel'),
      setValue: (value) => (value === true ? setFilter('recordTypeId', 2) : setFilter('recordTypeId', null)),
      input: {
        name: 'recordTypeId',
        id: 'recordTypeId',
      },
    },
    guaranteedPrice: {
      value: filtersState?.guaranteedPrice?.value ? filtersState.guaranteedPrice.value : false,
      error: filtersState?.guaranteedPrice?.error,
      label: t('guaranteedPriceLabel'),
      setValue: (value) => setFilter('guaranteedPrice', value),
      input: {
        name: 'guaranteedPrice',
        id: 'guaranteedPrice',
      },
    },
    sourceId: {
      value: filtersState?.sourceId?.value ? filtersState.sourceId.value : '',
      error: filtersState?.sourceId?.error,
      label: t('source'),
      setValue: (value) => {
        if (!value || filtersState?.concessionId?.value !== value) setFilter('concessionId', null);
        return setFilter('sourceId', parseInt(value, 10));
      },
      placeholder: t('sourceId'),
      options: sourcesOptions,
      input: {
        name: 'sourceId',
        id: 'sourceId',
        label: t('select_source'),
      },
    },
    nrmStatus: {
      value: filtersState?.nrmStatus?.value ? filtersState.nrmStatus.value.join(',') : '',
      error: filtersState?.nrmStatus?.error,
      label: t('subStatusFormLabel'),
      setValue: (value) => setFilter('nrmStatus', value.split(',')),
      placeholder: t('subStatusFormLabel'),
      options: nrmStatusOptionsSorted,
      input: {
        name: 'nrmStatus',
        id: 'nrmStatus',
        label: t('select'),
      },
    },
    nrmRecordId: {
      value: filtersState?.nrmRecordId?.value ? filtersState.nrmRecordId.value : '',
      error: filtersState?.nrmRecordId?.error,
      label: t('nrmRecordId'),
      setValue: (value) => setFilter('nrmRecordId', value.replace(/\s+/g, '')),
      placeholder: t('nrmRecordIdPlaceholder'),
      input: {
        name: 'nrmRecordId',
        id: 'nrmRecordId',
      },
    },
    cancelCases: {
      value: filtersState?.cancelCases?.value ? filtersState.cancelCases.value : '',
      error: filtersState?.cancelCases?.error,
      label: t('cancelCases'),
      setValue: (value) => setFilter('cancelCases', value),
      placeholder: t('cancelCasesPlaceholder'),
      options: cancelCaseOptions,
      input: {
        name: 'cancelCases',
        id: 'cancelCases',
        label: t('select'),
      },
    },
    carcheck: {
      value: filtersState?.carcheck?.value ? filtersState.carcheck.value : false,
      error: filtersState?.carcheck?.error,
      label: t('carcheck_label'),
      setValue: (value) => setFilter('carcheck', value),
      input: {
        name: 'carcheck',
        id: 'carcheck',
      },
    },
    stellaClub: {
      value: filtersState?.stellaClub?.value ? filtersState.stellaClub.value : false,
      error: filtersState?.stellaClub?.error,
      label: t('stellaClubLabel'),
      setValue: (value) => setFilter('stellaClub', value),
      input: {
        name: 'stellaClub',
        id: 'stellaClub',
      },
    },
    pushToCoverage: {
      value: filtersState?.pushToCoverage?.value ? filtersState?.pushToCoverage.value : false,
      error: filtersState?.pushToCoverage?.error,
      label: t('PUSH_TO_COVERAGE'),
      setValue: (value) => setFilter('pushToCoverage', value),
      input: {
        name: 'pushToCoverage',
        id: 'pushToCoverage',
      },
    },
    pushToStockSale: {
      value: filtersState?.pushToStockSale?.value ? filtersState.pushToStockSale.value : false,
      error: filtersState?.pushToStockSale?.error,
      label: t('PUSH_TO_STOCK_SALE'),
      setValue: (value) => setFilter('pushToStockSale', value),
      input: {
        name: 'pushToStockSale',
        id: 'pushToStockSale',
      },
    },
  };

  return { filters, searchWithFilters, resetFilters, mode, isOpen, toggle, handleValidate };
};
